import { useEffect, useRef } from 'react';
import { RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useOAuthPopup } from '@/hooks/useOAuthPopup';
import { getOAuthProvider } from '../oauthProviders';
import type { ConnectorFieldSchema } from '@/types/connector-schema';

interface Props {
  field: ConnectorFieldSchema;
  value: unknown;
  onChange: (v: unknown) => void;
  /** True when the connector currently has a stored OAuth token. Sourced from credentialFlags. */
  connected: boolean;
  /** Connected identity (email, portal name, etc.). Backend #140 surfaces this. Null until backend lands. */
  identity: string | null;
  /** Called after a successful Reconnect so the orchestrator can refetch connector details. */
  onReconnected: () => void;
  /** True when this field has unsaved changes (typically after a fresh Reconnect). */
  dirty?: boolean;
  disabled?: boolean;
  /** Connector instance name. When set, the OAuth hook forwards it to /authorize
   *  so the backend can persist new tokens server-side on /complete (#183). */
  connectorName?: string;
}

export function OAuthRow({ field, onChange, connected, identity, onReconnected, dirty, disabled, connectorName }: Props) {
  const oauthConfig = field.oauth_config as any;
  const providerKey = oauthConfig?.provider ?? field.name;
  const provider = getOAuthProvider(providerKey);

  const { startOAuth, isLoading, isConnected: hookConnected, result } = useOAuthPopup(
    oauthConfig ?? providerKey,
    { connectorName },
  );

  // Track which result we've already notified the parent about.
  const notifiedResultRef = useRef<string | null>(null);

  // When OAuth completes successfully, push credentials up via onChange + onReconnected.
  useEffect(() => {
    if (hookConnected && result?.success) {
      const resultId = result.portal_id ?? result.session_token ?? 'success';
      if (notifiedResultRef.current !== resultId) {
        notifiedResultRef.current = resultId;
        // On REAUTH (connector was already connected when the user clicked
        // Reconnect), the backend has already persisted the new tokens to
        // credentials_encrypted via /complete -- see GitLab issue #183. We
        // MUST NOT push credentials into form state, otherwise the save flow
        // folds them into a settings PATCH that clobbers the JSONB column and
        // resets neighbouring extraction settings to their pydantic defaults.
        //
        // Even if the backend somehow returns credentials here (defence in
        // depth against future regressions), we ignore them on reauth.
        //
        // On FIRST-TIME SETUP (the wizard flow, connector not yet connected),
        // the backend still returns credentials in the response and the wizard
        // form captures them so the create-connector mutation can use them.
        const isReauth = connected;
        if (!isReauth && result.credentials) {
          onChange(result.credentials);
        }
        onReconnected();
      }
    }
  }, [hookConnected, result, onChange, onReconnected, connected]);

  return (
    <div className="flex items-center gap-3 rounded-md border bg-card p-3">
      {/* Provider tile */}
      <span
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sm font-bold text-white"
        style={{ background: provider.brandColor }}
        aria-hidden="true"
      >
        {provider.letter}
      </span>

      {/* Status text area */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">
            {connected
              ? `Connected with ${provider.displayName}`
              : `Not connected to ${provider.displayName}`}
          </span>
          {connected && (
            <Badge variant="default" className="h-[18px] bg-success/15 text-success">
              <span aria-hidden="true" className="mr-1 text-[8px]">●</span> Active
            </Badge>
          )}
          {dirty && (
            <Badge variant="default" className="h-[18px] bg-[oklch(85%_0.13_75)] text-[oklch(35%_0.13_75)]">
              Edited
            </Badge>
          )}
        </div>
        {connected && identity && (
          <div className="text-xs text-muted-foreground truncate mt-0.5">{identity}</div>
        )}
      </div>

      {/* Action button */}
      {connected ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => startOAuth()}
          disabled={disabled || isLoading}
        >
          <RefreshCw className="mr-2 h-3.5 w-3.5" aria-hidden="true" /> Reconnect
        </Button>
      ) : (
        <Button
          size="sm"
          onClick={() => startOAuth()}
          disabled={disabled || isLoading}
        >
          <ExternalLink className="mr-2 h-3.5 w-3.5" aria-hidden="true" /> Connect with {provider.displayName}
        </Button>
      )}
    </div>
  );
}
